import { Injectable, signal } from '@angular/core';

import type { OutlookAttendee, OutlookContext, PaneInitializationContext } from './office-types';

export interface SimRoomLocation {
  readonly email: string;
  readonly name: string;
}

/**
 * The simulated Outlook host item — shared between the dev-harness compose
 * window (`/#/sim`) and SimOfficeAdapter. Mirrors exactly the state the real
 * host owns: the pane never mutates this directly, only through the adapter
 * (which is what makes the harness a faithful contract test).
 */
@Injectable({ providedIn: 'root' })
export class SimHostState {
  readonly context = signal<OutlookContext>('compose');
  readonly subject = signal('Q3 supplier review');
  readonly start = signal<Date>(defaultStart());
  readonly end = signal<Date>(new Date(defaultStart().getTime() + 30 * 60000));
  readonly rooms = signal<readonly SimRoomLocation[]>([]);
  readonly requiredAttendees = signal<readonly OutlookAttendee[]>([
    { name: 'Priya Shah', email: 'priya.shah@northwind.com' },
    { name: 'Anna Kowalska', email: 'anna.kowalska@ricoh.eu' },
  ]);
  readonly optionalAttendees = signal<readonly OutlookAttendee[]>([]);
  readonly organizer = signal<OutlookAttendee | null>(null);
  readonly officeThemeMode = signal<'dark' | 'light'>('light');
  /** The "signed-in Outlook user" of the mock host (null = anonymous host). */
  readonly userEmail = signal<string | null>(null);
  readonly initContext = signal<PaneInitializationContext | null>(null);
  /** Office.js event log — the harness surfaces every adapter call. */
  readonly log = signal<readonly string[]>([]);

  logCall(message: string): void {
    this.log.update((l) => [message, ...l].slice(0, 12));
  }
}

function defaultStart(): Date {
  const d = new Date();
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60, 0, 0);
  if (d.getHours() >= 17) d.setHours(9, 30, 0, 0);
  return d;
}
